import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import type { KioskLayoutOption } from '../../types/kiosk-flow-types';

interface LayoutSelectProps {
  value: string;
  layouts: KioskLayoutOption[];
  // Layouts picked by other rows — each XML can be used once
  usedLayouts: Set<string>;
  isLoading: boolean;
  // The value isn't in Asset Manager — only known once the list has loaded
  missing?: boolean;
  className?: string;
  disabled?: boolean;
  onChange: (layoutName: string) => void;
}

// Radix Select items can't have '' as a value, so "no layout" goes through this
const NO_LAYOUT = '__none__';
const NO_LAYOUT_LABEL = 'None (default layout)';

// Layout dropdown — one entry per XML name, with the screen sizes it exists in.
// The layout is optional: None leaves the step on the kiosk's built-in screen.
export function LayoutSelect({
  value,
  layouts,
  usedLayouts,
  isLoading,
  missing,
  className,
  disabled,
  onChange,
}: LayoutSelectProps) {
  // A saved layout that isn't in the list still shows, so the row keeps its value
  const notListed = value !== '' && !layouts.some((l) => l.layoutName === value);

  return (
    <Select
      // While loading, an empty row shows the loading placeholder instead of None
      value={value || (isLoading ? '' : NO_LAYOUT)}
      onValueChange={(next) => onChange(next === NO_LAYOUT ? '' : next)}
      disabled={disabled || (isLoading && !value)}
    >
      <SelectTrigger className={`h-8 ${className ?? ''}`} aria-label="Layout">
        <SelectValue placeholder="Loading layouts…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_LAYOUT}>
          <span className="text-gray-500">{NO_LAYOUT_LABEL}</span>
        </SelectItem>
        {notListed && (
          <SelectItem value={value}>
            <span>{value}</span>
            {missing && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">File missing</span>
            )}
          </SelectItem>
        )}
        {layouts.map((layout) => (
          <SelectItem
            key={layout.layoutName}
            value={layout.layoutName}
            disabled={layout.layoutName !== value && usedLayouts.has(layout.layoutName)}
          >
            <span>{layout.layoutName}</span>
            {layout.sizes.length > 0 && (
              <span className="ml-2 text-xs text-gray-400">{layout.sizes.join(' · ')}</span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
